import React, {forwardRef, useEffect, useRef, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import Link from '@salesforce/retail-react-app/app/components/link'
import {Tooltip} from '@chakra-ui/react'
import {
    Box,
    Skeleton as ChakraSkeleton,
    Checkbox,
    Text,
    useToast,
    Link as CustomLink,
    FormControl,
    FormLabel,
    IconButton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {Switch} from '@chakra-ui/react'
import {
    useCustomer,
    useCustomerId,
    useCustomerType,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {pxToRem} from '../../../../utills/pxToRem'
import {ToggleCard} from '../../../../components/toggle-card'
import {CheckIcon} from '@salesforce/retail-react-app/app/components/icons'
import {fonts} from '../../../../theme/foundations/fonts'
import {formatPhoneDisplay} from '../../../../utills/url'
import PropTypes from 'prop-types'

// eslint-disable-next-line react/prop-types
const Skeleton = forwardRef(({children, height, width, ...rest}, ref) => {
    const {data: customer} = useCurrentCustomer()
    const {isRegistered} = customer
    const size = !isRegistered
        ? {
              height,
              width
          }
        : {}
    return (
        <ChakraSkeleton ref={ref} isLoaded={!customer.isLoading} {...rest} {...size}>
            {children}
        </ChakraSkeleton>
    )
})
Skeleton.displayName = 'Skeleton'

const CommunicationPreferencesCard = ({personalInfoUpdated}) => {
    const [isEmailPreferenceEnabled, setIsEmailPreferenceEnabled] = useState(false)
    const [isNumPreferenceEnabled, setIsNumPreferenceEnabled] = useState(false)
    const [isEnabledPreference, setIsEnabledPreference] = useState(false)
    const [pauseEffect, setPauseEffect] = useState(false)
    const [changedPreference, setChangedPreference] = useState('')
    const {formatMessage} = useIntl()
    const headingRef = useRef(null)
    const toast = useToast()
    const customerId = useCustomerId()
    const updateCustomerMutation = useShopperCustomersMutation('updateCustomer')
    const didMountRef = useRef(false)
    const {isRegistered} = useCustomerType()

    const {data: customer, refetch} = useCustomer(
        {parameters: {customerId, c_getSubPreferences: true}},
        {
            enabled: !!customerId && isRegistered,
            onSuccess: () => {
                updatedComPreference()
            }
        }
    )

    const submit = async () => {
        setIsEnabledPreference(false)
        try {
            updateCustomerMutation.mutate(
                {
                    parameters: {
                        customerId,
                        c_setSubPreferences: true,
                        ...(changedPreference === 'SMS'
                            ? {c_addToSMSList: isNumPreferenceEnabled}
                            : {c_addToEmailList: isEmailPreferenceEnabled})
                    },
                    body: {}
                },
                {
                    onSuccess: async () => {
                        toast({
                            title: formatMessage({
                                defaultMessage: 'Communication Preferences updated',
                                id: 'profile_card.info.communication_preferences'
                            }),
                            status: 'success',
                            isClosable: true,
                            position: 'top-right'
                        })
                        headingRef?.current?.focus()
                        refetch()
                    },

                    onError: async (error) => {
                        setPauseEffect(true)
                        updatedComPreference()
                        setTimeout(() => {
                            setPauseEffect(false)
                        }, 300)
                        let errorBody
                        if (error?.response instanceof Response) {
                            try {
                                errorBody = await error.response.clone().json()
                            } catch {
                                const text = await error.response.text()
                                try {
                                    errorBody = JSON.parse(text)
                                } catch {
                                    errorBody = {message: text}
                                }
                            }
                        }

                        if (errorBody?.statusCode === 'SYS-1035') {
                            toast({
                                title: errorBody?.statusMessage,
                                status: 'error',
                                position: 'top-right'
                            })
                        }
                    }
                }
            )
        } catch (error) {
            toast({
                title: formatMessage({
                    defaultMessage: 'Some thing went wrong',
                    id: 'profile_card.info.some_thing_went_wrong'
                }),
                status: 'error',
                isClosable: true,
                position: 'top-right'
            })
        }
    }

    const handleSelectEmailPreference = () => {
        setChangedPreference('EMAIL')
        setIsEmailPreferenceEnabled((emailPres) => !emailPres)
        setIsEnabledPreference(true)
    }

    const handleToggleNumPreference = () => {
        setChangedPreference('SMS')
        setIsNumPreferenceEnabled((numPres) => !numPres)
        setIsEnabledPreference(true)
    }

    const messageFrequency = formatMessage(
        {
            id: 'communication_preferences.sms_disclaimer',
            defaultMessage:
                'Message frequency varies depending on order status. Message and data rates may apply. Consent is not required as a condition of purchase. Reply HELP for help or STOP to cancel. For SMS terms of service <termLink>click here</termLink>. For our Privacy Policy <privacyLink>click here</privacyLink>.'
        },
        {
            termLink: (linkText) => (
                <CustomLink as={Link} to="/content/terms.html" color={'cav_red'}>
                    {linkText}
                </CustomLink>
            ),
            privacyLink: (linkText) => (
                <CustomLink as={Link} to="/content/privacy-policy.html" color="cav_red">
                    {linkText}
                </CustomLink>
            )
        }
    )

    useEffect(() => {
        if (!didMountRef.current || pauseEffect) {
            didMountRef.current = true
            return
        }

        if (isEnabledPreference) {
            submit()
        }
    }, [isNumPreferenceEnabled, isEmailPreferenceEnabled, isEnabledPreference, pauseEffect])

    const updatedComPreference = () => {
        const consent = customer?.c_myCavendersCommsConsent
        switch (consent) {
            case 'BOTH':
                setIsEmailPreferenceEnabled(true)
                setIsNumPreferenceEnabled(true)
                break
            case 'SMS':
                setIsEmailPreferenceEnabled(false)
                setIsNumPreferenceEnabled(true)
                break
            case 'EMAIL':
                setIsEmailPreferenceEnabled(true)
                setIsNumPreferenceEnabled(false)
                break
            default:
                setIsEmailPreferenceEnabled(false)
                setIsNumPreferenceEnabled(false)
        }
    }

    useEffect(() => {
        updatedComPreference()
    }, [customer?.c_myCavendersCommsConsent])

    useEffect(() => {
        personalInfoUpdated && refetch()
    }, [personalInfoUpdated])

    if (!customer) return
    return (
        <ToggleCard
            id="myStore"
            title={
                <Skeleton ref={headingRef} tabIndex="-1">
                    <FormattedMessage
                        defaultMessage="Communication Preferences"
                        id="communication_preferences.title.communication_preferences"
                    />
                </Skeleton>
            }
            displayPreIcons={false}
            marginTop="0 important"
            titleStyles={{
                color: 'black',
                fontSize: {base: pxToRem(16), lg: pxToRem(18)},
                letterSpacing: {base: pxToRem(2), lg: pxToRem(3)},
                fontWeight: 500,
                lineHeight: 'normal',
                marginBottom: pxToRem(10)
            }}
            paddingLeft={{base: pxToRem(15), lg: pxToRem(18)}}
            paddingRight={{base: pxToRem(15), lg: pxToRem(18)}}
            marginLeft={{base: pxToRem(10), lg: 0}}
            marginRight={{base: pxToRem(10), lg: 0}}
        >
            <Text
                fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                color="black"
                lineHeight={'normal'}
                fontWeight={500}
                letterSpacing={pxToRem(1)}
                marginBottom={{base: pxToRem(5), lg: pxToRem(10)}}
            >
                {formatMessage({
                    id: 'communication_preferences.title.email_preferences',
                    defaultMessage: 'Email Preferences'
                })}
            </Text>
            <Box display={'flex'} gap={pxToRem(5)}>
                <Text
                    fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                    color="black"
                    lineHeight={'normal'}
                    fontWeight={400}
                    letterSpacing={pxToRem(1)}
                    marginBottom={{base: pxToRem(10), lg: pxToRem(10)}}
                >
                    {customer?.email}
                </Text>
                {customer?.email && (
                    <Tooltip
                        label={formatMessage({
                            id: 'communication_preferences.email.tooltip.msg',
                            defaultMessage:
                                'To edit or update your email address, please update your email in the Personal Information card on this page.'
                        })}
                        sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.85)',
                            color: 'white',
                            borderRadius: '0',
                            fontFamily: fonts.primary,
                            fontSize: {base: pxToRem(12), lg: pxToRem(13)},
                            fontWeight: '400',
                            letterSpacing: pxToRem(1),
                            lineHeight: 'normal',
                            fontsize: {base: pxToRem(12), lg: pxToRem(13)},
                            padding: pxToRem(10)
                        }}
                    >
                        <IconButton
                            height={pxToRem(15)}
                            width={pxToRem(15)}
                            minWidth={pxToRem(15)}
                            icon={
                                <img
                                    src={getAssetUrl('static/svg/circle_info.svg')}
                                    height="15"
                                    width="15"
                                    alt="Email Preferences"
                                />
                            }
                            variant="unstyled"
                        />
                    </Tooltip>
                )}
            </Box>
            <Checkbox
                name="emailPreference"
                isChecked={isEmailPreferenceEnabled}
                onChange={handleSelectEmailPreference}
                alignItems={'start'}
                sx={{'span.chakra-checkbox__control': {marginTop: pxToRem(6)}}}
            >
                <Text
                    marginBottom={0}
                    textTransform="none"
                    position={'relative'}
                    color={'cav_pdp_dark_gray'}
                    fontFamily={fonts.primary}
                    fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                    fontWeight={400}
                    letterSpacing={pxToRem(1)}
                    lineHeight={'normal'}
                >
                    <FormattedMessage
                        defaultMessage="Yes! I want to join My Cavender’s to receive exclusive offers and benefits via email!"
                        id="communication_preferences.label.same_as_shipping"
                    />
                    <CheckIcon
                        color="white"
                        position="absolute"
                        left="-20px"
                        top="5px"
                        width={pxToRem(15)}
                        height={pxToRem(15)}
                    />
                </Text>
            </Checkbox>
            {customer.phoneMobile && (
                <Box marginTop={{base: pxToRem(25), lg: pxToRem(20)}}>
                    <Text
                        fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                        color="black"
                        lineHeight={'normal'}
                        fontWeight={500}
                        letterSpacing={pxToRem(1)}
                        fontStyle={'normal'}
                        marginBottom={{base: pxToRem(5), lg: pxToRem(10)}}
                    >
                        {formatMessage({
                            id: 'communication_preferences.title.text_preferences',
                            defaultMessage: 'Text Preferences'
                        })}
                    </Text>
                    <Text
                        fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                        color="black"
                        lineHeight={'normal'}
                        fontWeight={500}
                        letterSpacing={pxToRem(1)}
                        fontStyle={'normal'}
                        marginBottom={{base: pxToRem(5), lg: pxToRem(10)}}
                    >
                        {formatMessage({
                            id: 'communication_preferences.title.text_phone_number',
                            defaultMessage: 'Phone Number'
                        })}
                    </Text>
                    <Text
                        fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                        color={'black'}
                        lineHeight={'normal'}
                        fontWeight={400}
                        letterSpacing={pxToRem(1)}
                        fontStyle={'normal'}
                        border={'1px solid'}
                        padding={pxToRem(10)}
                        borderColor={'black'}
                    >
                        {formatPhoneDisplay(customer?.phoneMobile)}
                    </Text>
                    <FormControl display="flex" marginTop={{base: pxToRem(25), lg: pxToRem(27)}}>
                        <Switch
                            id="communication_preferences_text"
                            isChecked={isNumPreferenceEnabled}
                            onChange={handleToggleNumPreference}
                        />
                        <FormLabel
                            htmlFor="communication_preferences_text"
                            ml={pxToRem(10)}
                            mb="0"
                            cursor={'pointer'}
                            fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                            color={'black'}
                            lineHeight={'normal'}
                            fontWeight={400}
                            letterSpacing={pxToRem(1)}
                            fontStyle={'normal'}
                        >
                            {formatMessage({
                                id: 'communication_preferences.number_enable.label',
                                defaultMessage: 'Text me order status updates via SMS'
                            })}
                        </FormLabel>
                    </FormControl>
                </Box>
            )}

            <Box>
                <Text
                    fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                    color={'cav_pdp_dark_gray'}
                    lineHeight={'normal'}
                    fontWeight={400}
                    letterSpacing={pxToRem(1)}
                    fontStyle={'normal'}
                    marginTop={{base: pxToRem(15), lg: pxToRem(16)}}
                    marginBottom={{base: 0, lg: 0}}
                >
                    {messageFrequency}
                </Text>
            </Box>
        </ToggleCard>
    )
}
CommunicationPreferencesCard.propTypes = {
    personalInfoUpdated: PropTypes.bool
}

export default CommunicationPreferencesCard
